// Frontend dispatch table for action descriptors. Every action either:
//
//   1. Goes to the backend (NEAR-side encoding, server-side proof
//      assembly, or backend-cached view).
//   2. Goes to bridge-sdk-js client-side (EVM / Sol / Starknet encoders
//      that need no server resource).
//
// The dispatcher decides per-action which path to take. The decision
// depends on (a) the action kind and (b) for log_metadata / fin_transfer,
// the OmniAddress chain prefix supplied in the form body.

import type { ActionDescriptor, ActionKind, ActionBuildResponse } from "@/types/dto";

/** Where the unsigned tx for this action is assembled. */
export type DispatchSite = "backend" | "frontend";

/** Static routing table — keyed by action kind only. For LogMetadata and
 *  IsTransferFinalised the choice ALSO depends on the form body's
 *  chain prefix, see `dispatchSite`.
 */
const STATIC_ROUTES: Partial<Record<ActionKind, DispatchSite>> = {
  // NEAR-side encoders the backend owns.
  "near-storage-deposit": "backend",
  "near-sign-transfer": "backend",
  "near-init-transfer": "backend",
  "near-sign-btc-transaction": "backend",
  "near-fin-transfer-btc": "backend",
  "init-near-to-bitcoin-transfer": "backend",
  "btc-cancel-withdraw": "backend",
  "get-bitcoin-address": "backend",

  // Frontend-built via bridge-sdk-js.
  "evm-fin-transfer": "frontend",
  "sol-fin-transfer": "frontend",
  "starknet-fin-transfer": "frontend",
  // near_fin_transfer: origin-chain proof (EVM light-client / Wormhole VAA)
  // + storage-deposit derivation + buildFinalization, signed on NEAR.
  "near-fin-transfer": "frontend",
  // deploy/bind token: EVM light-client proof of the source-chain event +
  // buildDeployToken/buildBindToken, signed on NEAR.
  "deploy-token": "frontend",
  "bind-token": "frontend",
  // fast-fin (relayer-economic): read the origin event, front liquidity,
  // sign ft_transfer_call. EVM origins only.
  "near-fast-fin-transfer": "frontend",
  // submit-btc: backend scrapes the init_transfer tx, frontend selects UTXOs
  // + builds submit_transfer_to_utxo_chain_connector, signed on NEAR.
  "near-submit-btc-transfer": "frontend",

  // Built on the BACKEND (real builders — NOT NeedsExternalProof stubs):
  // these reuse the BTC light-client proof / event scrape + broadcast that
  // live server-side, returning an unsigned NEAR tx or a view result.
  "btc-verify-withdraw": "backend",
  "btc-verify-active-utxo-management": "backend",
  "btc-request-refund": "backend",
  "btc-verify-refund-finalize": "backend",
  "btc-fin-transfer": "backend",

  // Genuinely-hard flows still stubbed with an actionable bridge-cli pointer
  // (no bridge-sdk-js primitive / rs-only selection algorithm):
  //   - btc-rbf-increase-gas-fee   (utxo_utils fee/output recompute)
  //   - active-utxo-management     (utxo_utils rebalance-to-limits)
  //   - near-fast-fin-transfer-from-utxo (UTXO-variant transfer_id envelope)
  "btc-rbf-increase-gas-fee": "backend",
  "active-utxo-management": "backend",
  "near-fast-fin-transfer-from-utxo": "backend",
};

/** Inspect the form body for a chain hint that routes hybrid actions. */
function chainPrefix(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const idx = value.indexOf(":");
  return idx > 0 ? value.slice(0, idx).toLowerCase() : null;
}

export function dispatchSite(kind: ActionKind, body: Record<string, unknown>): DispatchSite {
  if (kind === "log-metadata") {
    const prefix = chainPrefix(body.token);
    // Bare account form (no colon) = NEAR.
    if (!prefix || prefix === "near") return "backend";
    return "frontend";
  }
  if (kind === "is-transfer-finalised") {
    const dest = typeof body.destination_chain === "string" ? body.destination_chain : null;
    if (dest === "Near") return "backend";
    return "frontend";
  }
  return STATIC_ROUTES[kind] ?? "backend";
}

/** Convenience predicate — the action requires a connected wallet on
 *  the indicated chain. Used by the UI to gate the submit button. */
export function chainForAction(
  d: ActionDescriptor,
  body: Record<string, unknown>,
): "near" | "evm" | "sol" | "starknet" | "btc" | "view" {
  switch (d.kind) {
    case "log-metadata": {
      const prefix = chainPrefix(body.token);
      if (!prefix || prefix === "near") return "near";
      if (["eth", "arb", "base", "bnb", "pol", "abs"].includes(prefix)) return "evm";
      // Fogo is a separate SVM network but uses the same Solana-family wallet.
      if (prefix === "sol" || prefix === "solana" || prefix === "fogo") return "sol";
      if (prefix === "strk" || prefix === "starknet") return "starknet";
      return "view";
    }
    case "is-transfer-finalised": {
      const dest = body.destination_chain;
      if (dest === "Near") return "view";
      if (["Eth", "Arb", "Base", "Bnb", "Polygon", "Abs"].includes(String(dest))) return "view";
      if (dest === "Sol") return "view";
      if (dest === "Starknet") return "view";
      return "view";
    }
    case "evm-fin-transfer":
      return "evm";
    case "sol-fin-transfer":
      return "sol";
    case "starknet-fin-transfer":
      return "starknet";
    case "init-near-to-bitcoin-transfer":
    case "near-fin-transfer-btc":
    case "near-sign-btc-transaction":
    case "btc-cancel-withdraw":
    case "near-sign-transfer":
    case "near-init-transfer":
    case "near-storage-deposit":
    // near_fin_transfer + deploy/bind-token read the origin-chain proof over
    // RPC (no origin wallet needed) and only require the NEAR wallet to sign.
    case "near-fin-transfer":
    case "deploy-token":
    case "bind-token":
    case "near-fast-fin-transfer":
    case "near-submit-btc-transfer":
      return "near";
    case "get-bitcoin-address":
      return "view";
    default:
      return "near";
  }
}

/** Read a build response for which wallet should receive the next step. */
export function walletChainFromResponse(resp: ActionBuildResponse): "near" | "evm" | "sol" | "starknet" | "view" {
  switch (resp.kind) {
    case "unsigned_txs":
      return "near";
    case "unsigned_evm_tx":
      return "evm";
    case "unsigned_solana_tx":
      return "sol";
    case "unsigned_starknet_tx":
      return "starknet";
    case "view_result":
      return "view";
  }
}
